import { useState, useEffect } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import {
  QUARTER_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  LESSON_STAGE_OPTIONS_MENU,
} from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import { getSkillsDropdown } from '@/service/gameService';
import { useSettingsStore } from '@/store/useSettingsStore';

export interface IGameDashboardFilters {
  contentType: string;
  quarter: string;
  stage: string;
  skills: string | string[];
}

interface ISkillDropdownItem {
  id: string;
  title_en: string;
  title_ar: string;
}

interface IFilterDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialFilters?: IGameDashboardFilters | null;
  onApplyFilters?: (filters: IGameDashboardFilters) => void;
}

const EMPTY_FILTERS: IGameDashboardFilters = {
  contentType: '',
  quarter: '',
  stage: '',
  skills: [],
};

const FilterDrawer = ({
  isOpen,
  setIsOpen,
  initialFilters,
  onApplyFilters,
}: IFilterDrawerProps) => {
  const { formatText } = useFormatText();
  const language = useSettingsStore((state) => state.settings.language);

  const [skillOptions, setSkillOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isSkillsLoading, setIsSkillsLoading] = useState(false);

  const [contentType, setContentType] = useState<string>(
    initialFilters?.contentType || ''
  );
  const [quarter, setQuarter] = useState<string>(initialFilters?.quarter || '');
  const [stage, setStage] = useState<string>(initialFilters?.stage || '');
  const [skills, setSkills] = useState<string | string[]>(
    initialFilters?.skills || []
  );

  useEffect(() => {
    const fetchSkills = async () => {
      setIsSkillsLoading(true);
      try {
        const res = await getSkillsDropdown();
        if (res?.status && res?.data) {
          setSkillOptions(
            res.data.map((item: ISkillDropdownItem) => ({
              value: item.id,
              label: language === 'ar' ? item.title_ar : item.title_en,
            }))
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsSkillsLoading(false);
      }
    };

    if (isOpen) fetchSkills();
  }, [isOpen, language]);

  const handleClearFilters = () => {
    setContentType('');
    setQuarter('');
    setStage('');
    setSkills([]);
    onApplyFilters?.(EMPTY_FILTERS);
    setIsOpen(false);
  };

  const handleApplyFilters = () => {
    onApplyFilters?.({ contentType, quarter, stage, skills });
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      position="right"
      isHeader
      heading={formatText('create-lesson.filter.title')}
      isFooter
      dataTestId="filter-drawer"
      cancelButtonProps={{
        variant: 'link',
        label: formatText('common.clear-filters'),
        onClick: handleClearFilters,
      }}
      confirmButtonProps={{
        label: formatText('common.show-results'),
        onClick: handleApplyFilters,
        variant: 'primary',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        {/* Content type */}
        <RadioButtonGroup
          name="contentType"
          label={formatText('create-lesson.filter.content')}
          options={CONTENT_TYPE_OPTIONS}
          value={contentType}
          onChange={setContentType}
          direction="row"
          gap="gap-8"
          formatLabel={formatText}
        />

        {/* Quarter */}
        <Select
          name={formatText('create-lesson.filter.quarter')}
          options={QUARTER_OPTIONS}
          selected={quarter}
          onChange={(val) => setQuarter(val as string)}
          placeholder={formatText('create-lesson.filter.quarter-placeholder')}
          className="w-full"
          isClearable
        />

        {/* Stage */}
        <Select
          name={formatText('create-lesson.filter.stage')}
          options={LESSON_STAGE_OPTIONS_MENU}
          selected={stage}
          onChange={(val) => setStage(val as string)}
          placeholder={formatText('create-lesson.filter.stage-placeholder')}
          className="w-full"
          isClearable
          isTextFormat
        />

        {/* Skills */}
        <Select
          name={formatText('create-lesson.filter.skills')}
          options={skillOptions}
          selected={skills}
          onChange={setSkills}
          placeholder={formatText('create-lesson.filter.skills-placeholder')}
          className="w-full"
          isMulti
          isClearable
          isLoading={isSkillsLoading}
        />
      </div>
    </CustomModal>
  );
};

export default FilterDrawer;
